import React from 'react';
import { InboxMessage } from '../domain/types';
import { CheckCheck, MapPin, Phone, StickyNote, PlayCircle, Image as ImageIcon, Loader2 } from 'lucide-react';

interface Props {
  message: InboxMessage;
}

/** Status subtitle under outbound bubble content (no error reason). */
function outboundStatusLabel(message: InboxMessage): string | null {
  if (message.direction !== 'sent') return null;

  const delivery = message.delivery_status;
  if (message.status === 'failed' || delivery === 'failed') {
    return 'Mensaje fallido';
  }

  if (message.status === 'uploading' || message._id?.startsWith('temp')) {
    return 'Mensaje enviándose';
  }

  // Same as template outbound: once sent/delivered/read, no text — only ticks.
  return null;
  }

  function parseTemplateButtons(content: string) {
  const trailingButtons = content.match(
    /((?:\s*(?:\|\s*)?\[[^\[\]\r\n]+\]\s*)+(?:\|\s*)?)$/
  );

  if (!trailingButtons) {
    return {
      text: content,
      buttons: [] as string[],
    };
  }

  const buttons = Array.from(
    trailingButtons[1].matchAll(/\[([^\[\]\r\n]+)\]/g)
  )
    .map((match) => match[1].trim())
    .filter(
      (buttonText) =>
        buttonText &&
        !/^https?:\/\//i.test(buttonText) &&
        !/^www\./i.test(buttonText)
    );

    const text = content
      .slice(0, content.length - trailingButtons[1].length)
      .trimEnd();

    return {
      text,
      buttons,
    };
  }

  export const MessageCard: React.FC<Props> = ({ message }) => {
  let timeFormatted = '';
  try {
    if (message.received_at) {
      let str = message.received_at as any;
      if (typeof str === 'string') {
        if (!str.endsWith('Z') && !str.includes('+') && !/[+-]\d{2}:\d{2}$/.test(str)) {
          str += 'Z';
        }
      }
      const date = new Date(str);
      if (!isNaN(date.getTime())) {
        timeFormatted = date.toLocaleTimeString('en-US', {
          timeZone: 'America/Bogota',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        }).toLowerCase().replace(/\s/g, "");
      }
    }
  } catch (e) {
    console.error("[Inbox] MessageCard timestamp error:", e);
  }

    const isSent = message.direction === 'sent';
    const isUploading = message.status === 'uploading';
    const isFailed = message.status === 'failed' || message.delivery_status === 'failed';
    const statusLabel = outboundStatusLabel(message);

    const parsedContent =
      isSent && message.content
        ? parseTemplateButtons(message.content)
        : {
            text: message.content || '',
            buttons: [] as string[],
          };

    const structuredButtons = isSent
      ? (message.buttons || [])
          .map((button) => button.text?.trim())
          .filter((text): text is string => Boolean(text))
      : [];

    const parsedMessage = {
      text: parsedContent.text,
      buttons:
        structuredButtons.length > 0
          ? structuredButtons
          : parsedContent.buttons,
    };

    const renderContent = () => {
    if (isUploading) {
      return (
        <span className="flex items-center gap-2 text-xs italic text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Subiendo {message.message_type || 'archivo'}…
        </span>
      );
    }

    if (message.media_url) {
      if (message.message_type === 'audio') {
        return (
          <div className="mt-1">
            <audio controls preload="metadata" className="h-10 w-[200px] max-w-full">
               <source src={message.media_url} type="audio/ogg" />
               <source src={message.media_url} />
            </audio>
            {parsedMessage.text ? <p className="mt-1 text-sm">{parsedMessage.text}</p> : null}
          </div>
        );
      }
      if (
        message.message_type === 'image' ||
        message.message_type === 'sticker' ||
        message.message_type === 'template'
      ) {
        return (
          <div className="mt-1">
            <img 
              src={message.media_url} 
              alt={message.message_type === 'sticker' ? 'Sticker' : 'Media'} 
              className={`rounded-lg max-w-[250px] max-h-64 object-contain border border-white/20 shadow-sm ${
                message.message_type === 'sticker' ? 'bg-transparent' : 'object-cover'
              }`}
            />
            {parsedMessage.text ? <p className="mt-1 text-sm">{parsedMessage.text}</p> : null}
          </div>
        );
      }
      if (message.message_type === 'video') {
         return (
           <div className="mt-1">
             <video controls preload="metadata" className="rounded-lg max-w-[250px] max-h-64 border border-white/20 shadow-sm">
                <source src={message.media_url} type="video/mp4" />
                <source src={message.media_url} />
             </video>
             {parsedMessage.text ? <p className="mt-1 text-sm">{parsedMessage.text}</p> : null}
           </div>
         );
      }
      return (
        <a 
          href={message.media_url} 
          target="_blank" 
          rel="noopener noreferrer"
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border w-fit text-sm transition-colors ${
            isSent ? 'bg-white/10 border-white/20 text-white hover:bg-white/20' : 'bg-blue-50 border-blue-100 text-blue-600 hover:bg-blue-100'
          }`}
        >
          <span className="text-xl">📎</span>
          <span className="font-medium underline text-xs">Descargar {message.message_type}</span>
        </a>
      );
    }

    if (!parsedMessage.text && parsedMessage.buttons.length === 0) {
      const type = message.message_type || message.raw_message?.type || "unknown";
      return (
        <span className={`flex items-center gap-2 italic px-3 py-2 rounded-lg border mt-0.5 w-fit text-xs ${
          isSent ? 'bg-white/10 border-white/20 text-blue-50' : 'bg-gray-50 border-gray-100 text-gray-500'
        }`}>
           {type === 'audio' && <PlayCircle className="w-4 h-4" />}
           {type === 'image' && <ImageIcon className="w-4 h-4" />}
           {type === 'document' && <span className="text-lg">📄</span>}
           {type === 'sticker' && <StickyNote className="w-4 h-4" />}
           {type === 'location' && <MapPin className="w-4 h-4" />}
           {type === 'contacts' && <Phone className="w-4 h-4" />}
           {type === 'interactive' && <span className="text-lg">👆</span>}
           <span>{type}</span>
        </span>
      );
    }

    return <>{parsedMessage.text}</>;
  };

    return (
    <div
      className={`w-fit max-w-[90%] md:max-w-[75%] ${
        isSent ? 'ml-auto' : 'mr-auto'
      }`}
    >
      <div className={`relative rounded-2xl px-4 py-2.5 shadow-sm break-words ${
        isFailed
          ? 'bg-red-50 text-red-800 rounded-tr-none border border-red-200'
          : isSent
          ? 'bg-[#d9fdd3] text-gray-800 rounded-tr-none border border-[#c1f5b6]'
          : 'bg-white text-gray-800 rounded-tl-none border border-gray-200'
      }`}>
        {!isSent && message.campaign_name && (
          <div className="mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-primary opacity-80">
              {message.campaign_name}
            </span>
          </div>
        )}

        <div className="text-[14px] leading-relaxed whitespace-pre-wrap">
          {renderContent()}
        </div>

        {statusLabel && (
          <div
            className={`mt-1 text-[10px] font-medium ${
              isFailed ? 'text-red-600' : 'text-gray-500'
            }`}
          >
            {statusLabel}
          </div>
        )}

        <div className={`flex items-center justify-end gap-1 mt-1 ${
          isSent ? 'text-gray-500' : 'text-gray-400'
        }`}>
          <span className="text-[10px] font-medium block pt-0.5">
            {timeFormatted}
          </span>

          {isSent && !isUploading && !isFailed && (
            <CheckCheck className={`w-3.5 h-3.5 ${
              message.delivery_status === 'read' || message.delivery_status === 'delivered'
                ? 'text-blue-500'
                : 'text-gray-400'
            }`} />
          )}
        </div>
      </div>

      {isSent && parsedMessage.buttons.length > 0 && (
        <div className="mt-[2px] overflow-hidden rounded-lg bg-white shadow-sm divide-y divide-gray-200">
          {parsedMessage.buttons.map((buttonText, index) => (
            <div
              key={`${buttonText}-${index}`}
              className="flex min-h-[40px] w-full max-w-full min-w-0 items-center justify-center px-4 py-2.5 text-center text-[14px] font-medium text-[#00A884] whitespace-normal break-words [overflow-wrap:anywhere] select-none"
            >
              {buttonText}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
